import { useEffect, useState } from 'react';
import { APIClientUnreadSnapshot, WSS_URL } from '../api';

export function useClientChatUnread(account, enabled = true) {
  const [snapshot, setSnapshot] = useState({ actorId: null, token: null, counts: {}, ready: false });
  const token = account?.id ? localStorage.getItem('client_token') : null;
  useEffect(() => {
    let disposed = false, busy = false, dirty = false, debounce = null;
    let socket = null, reconnect = null, heartbeat = null, attempts = 0, lastPong = 0;
    setSnapshot({ actorId: account?.id, token, counts: {}, ready: false });
    if (!account?.id || !token || !enabled) return undefined;
    const alive = () => !disposed && localStorage.getItem('client_token') === token;
    const sync = async () => {
      dirty = true;
      if (busy || !alive()) return;
      busy = true;
      try {
        while (dirty && alive()) {
          dirty = false;
          const data = await APIClientUnreadSnapshot();
          if (!alive()) break;
          if (dirty) continue;
          if (!Array.isArray(data?.unread_counts)) throw new Error('Некорректный счётчик');
          const counts = Object.fromEntries(data.unread_counts.map(r => [String(r.chat_id), Math.max(0, Number(r.unread_count) || 0)]));
          setSnapshot({ actorId: account.id, token, counts, ready: true });
        }
      } catch (error) { if (alive()) console.error('Не удалось обновить непрочитанные', error); }
      finally { busy = false; }
    };
    const schedule = () => {
      dirty = true;
      if (debounce !== null || !alive()) return;
      debounce = setTimeout(() => { debounce = null; sync(); }, 150);
    };
    const connect = () => {
      if (!alive()) return;
      const ws = new WebSocket(`${WSS_URL}/notifications/?token=${encodeURIComponent(token)}`);
      socket = ws;
      ws.onopen = () => { attempts = 0; lastPong = Date.now(); schedule(); };
      ws.onmessage = event => {
        if (!alive()) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') lastPong = Date.now();
          if (['initial_data', 'unread_changed', 'new_message', 'message_read', 'new_chat'].includes(data.type)) schedule();
        } catch { /* не используем неполный пакет как снимок */ }
      };
      ws.onerror = () => ws.close();
      ws.onclose = () => {
        if (alive() && socket === ws) reconnect = setTimeout(connect, Math.min(30000, 1000 * 2 ** Math.min(attempts++, 5)));
      };
    };
    const wake = () => { if (document.visibilityState === 'visible') schedule(); };
    const read = () => schedule();
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    window.addEventListener('client_chat_read', read);
    document.addEventListener('visibilitychange', wake);
    const poll = setInterval(wake, 60000);
    heartbeat = setInterval(() => {
      if (socket?.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastPong > 65000) { socket.close(); return; }
      socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
    }, 20000);
    sync(); connect();
    return () => {
      disposed = true;
      clearTimeout(debounce); clearTimeout(reconnect); clearInterval(poll); clearInterval(heartbeat);
      socket?.close();
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('client_chat_read', read);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [account?.id, token, enabled]);
  return snapshot.actorId === account?.id && snapshot.token === token && enabled
    ? snapshot : { counts: {}, ready: false };
}